// @flow
import React from 'react';
import useSheet from 'app/jss';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import Avatar from 'app/components/commons/Avatar';
import ConfirmBox from 'app/components/commons/ConfirmBox';
import FriendBannerInfo from 'app/components/friends/banners/FriendsBannerInfo';
import BouncyLoader from 'app/components/commons/BouncyLoader';
import Icon from 'app/components/commons/Icon';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import t from 'app/utils/translate';

type Props = {
  friend: iFriendUser,
  isDone?: boolean,
  isCreating?: boolean,
  onCancel: () => void,
  onConfirm: () => void,
  classes: Object,
};

class InviteFriendMobile extends React.Component {
  props: Props;

  static defaultProps = {
    isDone: false,
    isCreating: false,
  };

  render() {
    const { friend, onCancel, onConfirm, classes, isDone, isCreating } = this.props;

    return (
      <FriendBannerInfo icon="icon-invite-friend">
        <div className={classes.textContainer}>
          <span className={classes.heading}>UH OH!</span>
          <span className={classes.subheading}>
            {t("It seems the friend you're looking for is not registered yet.")}
          </span>
        </div>
        <div className={classes.content}>
          <div className={classes.avatarBox}>
            <Avatar status={friend.status} size="small" avatar={friend.avatar} />
            <span className={classes.username}>{friend.username}</span>
          </div>
          <AnimatedGroup onEnter={Enter.appear(0.3, 0.3)} onLeave={Leave.disappear(0.3)}>
            {!isCreating &&
              !isDone && (
                <div className={classes.bottomContainer} key="send">
                  <span className={classes.question}>
                    {t('Do you want to send an invitation?')}
                  </span>
                  <ConfirmBox
                    className={classes.confirmBox}
                    onConfirm={onConfirm}
                    gap={50}
                    size={50}
                  />
                </div>
              )}
            {isDone && (
              <div className={classes.bottomContainer} key="done">
                <span className={classes.question}>{t('Request Sent!')}</span>
                <Icon type="icon-tick-circle" size="50px" className={classes.doneTick} />
              </div>
            )}
            {isCreating && (
              <div className={classes.bottomContainer} key="creating">
                <span className={classes.question}>{t('Sending your request')}</span>
                <div className={classes.loader}>
                  <BouncyLoader scheme="dark" />
                </div>
              </div>
            )}
          </AnimatedGroup>
        </div>
      </FriendBannerInfo>
    );
  }
}

function applyStyles(theme: Theme) {
  const avatarBoxWidth = 140;
  return {
    loader: {
      width: 40,
    },
    doneTick: {
      color: theme.color.primary,
    },
    textContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing.default,
      alignItems: 'center',
      textAlign: 'center',
      [theme.mediaQuery.desktopAndUp]: {
        width: 300,
      },
    },
    heading: {
      fontSize: theme.fontSize.large,
      color: theme.color.basis6,
    },
    username: {
      fontSize: theme.fontSize.xxsmall,
      textOverflow: 'ellipsis',
      maxWidth: '90%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      marginTop: 5,
    },
    subheading: {
      fontSize: theme.fontSize.medium,
      color: theme.color.basis4,
      paddingLeft: theme.spacing.small,
      paddingRight: theme.spacing.small,
      [theme.mediaQuery.desktopAndUp]: {
        color: theme.color.basis4,
        fontSize: theme.fontSize.small,
      },
    },
    content: {
      flexGrow: 1,
      margin: theme.spacing.xlarge,
      marginTop: 50,
      borderRadius: 10,
      backgroundColor: theme.color.basis6,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      zIndex: 1,
      [theme.mediaQuery.desktopAndUp]: {
        width: 250,
        height: 250,
        marginTop: 70,
        flexGrow: 0,
      },
    },
    avatarBox: {
      position: 'absolute',
      top: 0,
      transform: 'translateY(-33%)',
      width: avatarBoxWidth,
      height: avatarBoxWidth,
      backgroundColor: theme.color.white,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    question: {
      textTransform: 'uppercase',
      color: theme.color.basis2,
      fontWeight: 600,
      fontSize: theme.fontSize.medium,
      marginBottom: 10,
      [theme.mediaQuery.desktopAndUp]: {
        padding: theme.spacing.default,
        fontSize: theme.fontSize.xsmall,
        textAlign: 'center',
      },
    },
    bottomContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50%',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
    },
    confirmBox: {
      fontSize: theme.fontSize.large,
    },
  };
}

export default useSheet(applyStyles)(InviteFriendMobile);
